// Copyright © 2019-2026 Focal Systems. All rights reserved.

/**
 * Encode/decode filter state as a single base64 URL search param.
 * Uses a fixed-order array keyed by `defaults` key order for deterministic output.
 * Only non-default values are encoded to keep the string short.
 */

function isEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((v, i) => v === b[i]);
  }
  return false;
}

// btoa/atob only cover Latin-1 (one byte per char), so any code point above U+00FF
// (Polish, Cyrillic, CJK, emoji, …) makes btoa throw. Encode via UTF-8 bytes first so
// arbitrary search text round-trips. For pure-ASCII input this is byte-identical to the
// old btoa(str), keeping previously-generated URLs valid.
function base64EncodeUtf8(str: string): string {
  const bytes = new TextEncoder().encode(str);
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

function base64DecodeUtf8(encoded: string): string {
  const binary = atob(encoded);
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

export function encodeFilters<T extends Record<string, unknown>>(filters: T, defaults: T): string {
  const keys = Object.keys(defaults);
  const values: unknown[] = [];
  let hasNonDefault = false;

  for (const key of keys) {
    const val = filters[key];
    if (!isEqual(val, defaults[key])) {
      values.push(val);
      hasNonDefault = true;
    } else {
      values.push(null);
    }
  }

  if (!hasNonDefault) return "";
  return base64EncodeUtf8(JSON.stringify(values)).replaceAll("=", "");
}

export function decodeFilters<T extends Record<string, unknown>>(encoded: string | undefined, defaults: T): T {
  if (!encoded) return { ...defaults };
  try {
    const values = JSON.parse(base64DecodeUtf8(encoded)) as unknown[];
    const keys = Object.keys(defaults);
    const result = { ...defaults };
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      if (key !== undefined && i < values.length && values[i] !== null) {
        (result as Record<string, unknown>)[key] = values[i];
      }
    }
    return result;
  } catch {
    return { ...defaults };
  }
}
